import { useEffect, useRef } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useSocket } from "@/providers/socket-provider";
import { getReportStatus, type GetReportStatusResponse } from "@/api/gen-api";
import { toast } from "sonner";

interface ReportStatusUpdatePayload {
  companyId: string;
  opportunityId: string;
  reportStatus: Record<string, string>;
  changedField: string;
  newStatus: string;
}

const REPORT_NAME_MAP: Record<string, string> = {
  summary: "Deal Brief",
  tearsheet: "Tearsheet",
  startupscore: "Business Risk",
  datasufficiencyscore: "Data Sufficiency Score",
  datasufficiencyscore_v2: "Data Sufficiency Score",
  deal_info: "Deal Info",
  deal_info_ingest: "Deal Info",
  metrics: "Metrics",
  website_summary: "Website Summary",
  website_summary_ingest: "Website Summary",
  website_summary_v2: "Website Summary",
  website_summary_v2_ingest: "Website Summary",
  linkedin_company_scrape: "LinkedIn",
  linkedin_company_ingest: "LinkedIn",
  gap_analysis: "Gap Analysis",
  deal_fit: "Deal Fit",
  insights: "Insights",
  market_research: "Market Research",
  market_research_ingest: "Market Research",
};

function formatReportName(field: string): string {
  return REPORT_NAME_MAP[field] || field.replace(/_/g, " ");
}

// An ingest field represents source data being loaded (file upload, metadata
// edit, LinkedIn scrape), not an AI report being generated. Dynamic keys like
// per-file types (e.g. "company pitchdeck") are absent from REPORT_NAME_MAP and
// also originate from ingestion, so anything unknown falls into this bucket.
function isIngestField(field: string): boolean {
  if (field.endsWith("_ingest")) return true;
  if (field === "linkedin_company_scrape") return true;
  return !(field in REPORT_NAME_MAP);
}

const TOAST_DEDUPE_WINDOW_MS = 5000;

export function useReportStatusSocket(
  companyId?: string,
  workspaceId?: string
) {
  const queryClient = useQueryClient();
  const { socket, isConnected, fallbackToPolling } = useSocket();
  const recentToastsRef = useRef<Map<string, number>>(new Map());

  // Emits a completion/failure toast, deduped by (displayName + status) within
  // a short window. Multiple BE fields can map to the same user-facing report
  // name (e.g., linkedin_company_scrape + linkedin_company_ingest → "LinkedIn"),
  // so the user should see only one toast per display name.
  const emitReportToast = (field: string, status: string) => {
    if (status !== "completed" && status !== "failed") return;
    // Ingest fields are internal data-loading steps (file parses, scrapes,
    // migrations) — the user never requested them and can't act on a failure
    // here. Any real downstream impact will fire its own report toast.
    if (isIngestField(field)) return;
    const displayName = formatReportName(field);
    const dedupeKey = `${displayName}::${status}`;
    const now = Date.now();
    const lastShown = recentToastsRef.current.get(dedupeKey);
    if (lastShown && now - lastShown <= TOAST_DEDUPE_WINDOW_MS) return;
    recentToastsRef.current.set(dedupeKey, now);
    if (status === "completed") {
      toast.success(`${displayName} report is ready`);
    } else {
      toast.error(`${displayName} generation failed`);
    }
  };

  // Listen for socket events and update React Query cache
  useEffect(() => {
    if (!socket || !isConnected || !companyId || !workspaceId) return;

    const handler = (data: ReportStatusUpdatePayload) => {
      // Only handle events for this specific deal
      if (data.companyId !== companyId || data.opportunityId !== workspaceId)
        return;

      // `_id` is the report_status subdocument id, not a report. Ignore it so
      // a stray emit can't churn the cache and collapse the
      // in_progress→completed window that async reports rely on to auto-refresh.
      if (data.changedField === "_id") return;

      // Update the React Query cache directly
      queryClient.setQueryData<GetReportStatusResponse>(
        ["reportStatus", companyId, workspaceId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            opportunityApplication: {
              ...old.opportunityApplication,
              report_status: {
                ...old.opportunityApplication.report_status,
                [data.changedField]: data.newStatus,
              },
            },
          };
        }
      );

      emitReportToast(data.changedField, data.newStatus);

      // When a report completes, invalidate the report data query so it refetches
      if (data.newStatus === "completed") {
        queryClient.invalidateQueries({
          queryKey: ["reportData", companyId, workspaceId],
        });
      }
    };

    socket.on("report:status_update", handler);
    return () => {
      socket.off("report:status_update", handler);
    };
  }, [socket, isConnected, companyId, workspaceId, queryClient]);

  // Initial fetch (one-time) + fallback polling if socket is down
  const query = useQuery({
    queryKey: ["reportStatus", companyId, workspaceId],
    queryFn: () => {
      if (!companyId || !workspaceId) return null;
      return getReportStatus(companyId, workspaceId);
    },
    enabled: !!companyId && !!workspaceId,
    refetchInterval: fallbackToPolling ? 3000 : false,
  });

  // Toast emitter for the polling fallback path. The socket handler above
  // owns toasts when the socket is connected; this effect only runs when we
  // fall back to polling so the user still sees completion/failure toasts.
  const previousStatusRef = useRef<Record<string, string>>({});
  const hasInitializedRef = useRef(false);

  // Reset the toast tracking when the deal changes OR when the polling
  // fallback toggles. Without the toggle reset, a stale snapshot from a prior
  // polling session would diff against fresh cache data (mutated by the
  // socket handler in between) and re-toast every change. This effect MUST
  // run before the polling effect below — React runs effects in declaration
  // order, so on a false→true toggle the reset clears the refs first and the
  // polling effect then snapshots into clean state.
  useEffect(() => {
    previousStatusRef.current = {};
    hasInitializedRef.current = false;
    // Also clear the toast dedupe tracking so stale entries from a previous
    // deal don't suppress a fresh completion toast on the new deal.
    recentToastsRef.current.clear();
  }, [companyId, workspaceId, fallbackToPolling]);

  useEffect(() => {
    if (!fallbackToPolling) return;

    const current = (query.data as GetReportStatusResponse | null | undefined)
      ?.opportunityApplication?.report_status as
      | Record<string, string>
      | undefined;
    if (!current) return;

    // First poll: just snapshot, don't toast (otherwise every existing
    // "completed" status would fire on initial load).
    if (!hasInitializedRef.current) {
      previousStatusRef.current = { ...current };
      hasInitializedRef.current = true;
      return;
    }

    const prev = previousStatusRef.current;
    for (const [field, value] of Object.entries(current)) {
      if (prev[field] === value) continue;
      emitReportToast(field, value);
    }
    previousStatusRef.current = { ...current };
  }, [query.data, fallbackToPolling]);

  return query;
}
